import { useTags } from "~/api/swr/useTags";
import type { UserSummary } from "~/api/types";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import { DocumentArtifactStatus } from "~/config/types";
import { toDate } from "~/helpers/date";
import { printFullName } from "~/helpers/user";
import { useUserContext } from "~/hooks/useUserContext";

export interface ApplyTemplateRow {
  id: number;
  title: string;
  category: { id: number; title: string };
  tagIds: number[];
  currentTemplateName: string | null;
  currentTemplateId: number | null;
  /** The artifact's current workflow description, replaced by the template unless kept. */
  currentDescription: string | null;
  owner: UserSummary;
  ownerName: string;
  tier: string | null;
  reviewFrom: Date | null;
  reviewTo: Date | null;
}

const DRAFT_STATUSES = [DocumentArtifactStatus.draft];

export function useApplyTemplateRows() {
  const {
    data: releases,
    error,
    isLoading,
  } = useDocumentReleases({
    statuses: DRAFT_STATUSES,
    includeWorkflowData: true,
  });
  const { data: tags } = useTags();
  const { user: currentUser } = useUserContext();

  const tagMap = new Map((tags ?? []).map((tag) => [tag.id, tag.title]));

  const rows: ApplyTemplateRow[] = (releases ?? [])
    .filter((release) => !release.artifact.workflow?.isOngoing)
    .map((release) => {
      const { owner } = release.document;

      return {
        id: release.artifact.id,
        title: release.artifact.title,
        category: release.document.category,
        tagIds: release.document.tagIds,
        currentTemplateId:
          release.artifact.workflow?.workflowTemplateId ?? null,
        currentTemplateName:
          release.artifact.workflow?.workflowTemplateName ?? null,
        currentDescription: release.artifact.workflow?.description ?? null,
        owner,
        ownerName: printFullName(owner, owner.userId === currentUser.id),
        tier: release.document.tier?.title ?? null,
        reviewFrom: toDate(release.artifact.reviewFrom),
        reviewTo: toDate(release.artifact.reviewTo),
      };
    });

  return { rows, tagMap, isLoading, error };
}
